/**
 * SumBadge Component
 *
 * Displays booking hours sum with status-based coloring.
 * Uses the same visual state system as DayCell for consistency.
 *
 * Features:
 * - Status-based background colors (same as cells)
 * - Optional label prefix for contextual display
 * - Size variants for different contexts (schedule cells vs sidebar)
 * - Consistent with booking visual state rules
 *
 * Note: Unlike DayCell, badges do NOT show absence fill overlay
 * to keep them compact and readable.
 *
 * @example
 * ```tsx
 * // Simple usage in schedule cells
 * <SumBadge hours={8} status="fullyBooked" />
 *
 * // With label in sidebar
 * <SumBadge hours={0} status="fullyBooked" label="Ledig:" size="md" />
 * <SumBadge hours={5} status="overbooked" label="Overbooka:" size="md" />
 * ```
 */

import { formatHours } from "@/utils/date";
import classNames from "classnames";
import React from "react";
import styles from "./SumBadge.module.css";
import type { SumBadgeProps } from "./types";

export const SumBadge: React.FC<SumBadgeProps> = ({
  hours,
  status = "free",
  label,
  size = "sm",
  className,
  "data-testid": testId,
}) => {
  return (
    <span
      className={classNames(styles.badge, className)}
      data-status={status}
      data-size={size}
      data-testid={testId}
    >
      {label && <span className={styles.label}>{label}</span>}
      <span className={styles.text}>{formatHours(hours)}</span>
    </span>
  );
};

SumBadge.displayName = "SumBadge";

export default SumBadge;
